import React, { createContext, useCallback, useEffect, useReducer, useState } from 'react'
import { jwtDecode } from 'jwt-decode'
import log from 'loglevel'
import LocalStorage from '../services/LocalStorage'
import Raw from '../services/Raw'
import { useRef } from 'react'


const AuthContext = createContext()
export default AuthContext

export function AuthProvider(props) {

    const jwtRef = useRef(LocalStorage.getItem('auth')?.jwt)
    const refreshTimeoutRef = useRef(null)
    const refreshPromiseRef = useRef(null)

    // state
    const [busy, setBusy] = useState(false)
    const [{
        jwt,
        email,
        rememberedEmail,
        loginType,
    }, dispatch] = useReducer(authReducer, getInitialAuthState())

    // LOGIN
    const logIn = useCallback((email, password, remember, handleError) => {
        setBusy(true)
        Raw.getJwt(email, password)
            .then((jwt) => {
                dispatch({
                    type: 'login',
                    payload: {
                        jwtRef,
                        email,
                        jwt,
                        remember,
                        loginType: 'email',
                    },
                })
            })
            .catch((e) => {
                handleError(e.status)
            })
            .finally(() => {
                setBusy(false)
            })
    }, [])

    const getSsoRedirect = useCallback(async (email, remember, handleError) => {
        setBusy(true)

        try {
            const response = await Raw.fetchJson('POST', '/api/sso/login', {
                body: {
                    email,
                },
            })

            dispatch({
                type: 'manage-remembered-email',
                payload: {
                    jwtRef,
                    email,
                    remember,
                    loginType: 'sso',
                },
            })

            // redirect to link
            if (response.redirectUrl) window.location.href = response.redirectUrl

        } catch (e) {
            handleError(e.status)
        }
        setBusy(false)
    }, [])

    const logInSso = useCallback((code, handleError) => {
        setBusy(true)

        Raw.getJwtViaSso(code)
            .then((jwt) => {
                dispatch({
                    type: 'login',
                    payload: {
                        jwtRef,
                        email: rememberedEmail ? rememberedEmail : undefined,
                        jwt,
                        remember: !!rememberedEmail,
                        loginType: 'sso',
                    },
                })
            })
            .catch((e) => {
                handleError(e.status)
            })
            .finally(() => {
                setBusy(false)
            })
    }, [email, rememberedEmail])

    // LOGOUT
    const logOut = useCallback(() => {
        dispatch({
            type: 'logout',
            payload: { jwtRef }
        })
    }, [])

    const clearScheduledRefresh = useCallback(() => {
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current)
            refreshTimeoutRef.current = null
        }
    }, [])

    // shared, de-duplicated refresh - the proactive timer, the visibility check, and a
    // live 401 from any request can all trigger this concurrently; they share one in-flight
    // request instead of each firing their own
    const refreshJwt = useCallback((fail = false) => {
        if (refreshPromiseRef.current) return refreshPromiseRef.current

        log.warn('AUTH: attempting to refresh jwt...')

        const promise = Raw.getRefreshedJwt(jwtRef.current + ((fail) ? 'sfds' : ''))
            .then((newJwt) => {
                log.info('AUTH: refreshed jwt')
                dispatch({
                    type: 'refresh-jwt',
                    payload: {
                        jwtRef,
                        jwt: newJwt,
                    },
                })
                return newJwt
            })
            .catch((error) => {
                log.warn('AUTH: current jwt is not valid anymore, could not refresh auth')
                logOut()
                throw error
            })
            .finally(() => {
                refreshPromiseRef.current = null
            })

        refreshPromiseRef.current = promise
        return promise
    }, [logOut])

    // checks the current jwt's expiry and either logs out, refreshes right away, or
    // (re)schedules a refresh for later. Always clears any previously scheduled timer
    // first, so no matter which caller (the jwt-change effect, or the visibility check)
    // runs this, we never end up with more than one pending timer at a time
    const checkJwtExpiry = useCallback(() => {
        clearScheduledRefresh()

        const currentJwt = jwtRef.current
        if (!currentJwt) return

        try {
            const { exp } = jwtDecode(currentJwt)

            // if token is expired, log out
            if (secondsToMs(exp) <= Date.now()) {
                log.warn('AUTH: current jwt is expired, logging out...')
                logOut()
                return
            }

            // we want to refresh a jwt when it is 30 mins before expiring or later
            const shouldRefreshAtMs = secondsToMs(exp) - minuteToMs(30)

            // if shouldRefreshAtMs is in the past, refresh right away instead of scheduling
            const msUntilRefresh = Math.max(shouldRefreshAtMs - Date.now(), 0)

            if (msUntilRefresh === 0) {
                refreshJwt().catch(() => { })
                return
            }

            refreshTimeoutRef.current = setTimeout(() => {
                refreshTimeoutRef.current = null
                refreshJwt().catch(() => { })
            }, msUntilRefresh)
        } catch (e) {
            log.error(e)
            logOut()
        }
    }, [logOut, refreshJwt, clearScheduledRefresh])

    // (re)schedule whenever the jwt changes - login, a completed refresh, or logout
    useEffect(() => {
        checkJwtExpiry()
        return () => clearScheduledRefresh()
    }, [jwt, checkJwtExpiry, clearScheduledRefresh])

    // background tabs get their timers throttled (or fully frozen), so the scheduled
    // check above can be delayed well past the jwt's real expiry - re-check immediately
    // when the tab becomes visible again instead of waiting for it to catch up
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log('visibility change: rechecking JWT expiry')
                checkJwtExpiry()
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
    }, [checkJwtExpiry])

    return (
        <AuthContext.Provider
            value={{
                hasAuth: !!jwt,
                jwt: jwtRef,
                email,
                rememberedEmail,
                loginType,
                busy,
                logIn,
                getSsoRedirect,
                logInSso,
                logOut,
                refreshJwt,
            }}
            {...props}
        />
    )
}

function minuteToMs(mins) {
    return mins * 60 * 1000
}

function secondsToMs(seconds) {
    return seconds * 1000
}

const authLocalStorageKey = 'auth'

function getInitialAuthState() {
    return LocalStorage.getItem(authLocalStorageKey) || {}
}

// every time this reducer runs (aka: auth state changes),
// we write to localStorage, this way it'll _always_ be in sync with in-mem state
function authReducer(state, { type, payload }) {
    function calcState() {
        switch (type) {
            case 'login':
                payload.jwtRef.current = payload.jwt
                return {
                    jwt: payload.jwt,
                    email: payload.email,
                    rememberedEmail: payload.remember ? payload.email : undefined,
                    loginType: payload.loginType,
                    stickySession: payload.remember ? 'true' : '',
                }
            case 'logout':
                payload.jwtRef.current = null
                return {
                    rememberedEmail: state.rememberedEmail,
                    loginType: state.loginType,
                    stickySession: '',
                }
            case 'refresh-jwt':
                payload.jwtRef.current = payload.jwt
                return {
                    ...state,
                    jwt: payload.jwt,
                }
            case 'manage-remembered-email':
                return {
                    rememberedEmail: payload.remember ? payload.email : undefined,
                    loginType: payload.loginType,
                    stickySession: payload.remember ? 'true' : '',
                }
            default:
                throw new Error('invalid action')
        }
    }
    const newState = calcState()
    LocalStorage.setItem(authLocalStorageKey, newState)
    return newState
}
